import React from "react";
import { useNavigate } from "react-router-dom";
import "./legal.style.css";

const SECTIONS = [
  {
    number: "1",
    title: "About this Privacy Policy",
    body: "This Privacy Policy explains how UALIGN collects, uses, stores, shares, and protects personal data when you use our website, dentist portal, patient app, QR code system, treatment planning services, customer support, or related services.\n\nUALIGN handles information relating to dental treatment. Some of this information may be health-related data, which is treated as special category data under UK data protection law.",
  },
  {
    number: "2",
    title: "Who we are",
    body: "UALIGN LTD is a UK-based provider of digital clear aligner services, including digital treatment planning, custom aligner manufacturing, patient app support, and related services for dental professionals and patients.\n\nUALIGN LTD\nCompany number: 17062684\nRegistered in England and Wales\nRegistered office: 128, City Road, London, EC1V 2NX, UNITED KINGDOM\nEmail: info@ualign.co.uk\nPhone: +44 7537 135002\nWebsite: https://ualign.co.uk\nPrivacy and data protection contact: info@ualign.co.uk",
  },
  {
    number: "3",
    title: "Our role: controller or processor",
    body: "Depending on the situation, UALIGN may act as either a data controller or a data processor.\n\nWhen a dentist or clinic submits patient records to UALIGN for treatment planning or aligner production, the dentist or clinic is usually the data controller, and UALIGN acts as a processor acting on the clinic's instructions.\n\nUALIGN may act as a controller for website visitors, dentist or clinic accounts, business communications, billing, customer support, app account administration, legal and regulatory records, quality and security records, and marketing communications where permitted.",
  },
  {
    number: "4",
    title: "Personal data we collect",
    body: "For dentists, clinics, distributors, and business contacts, we may collect name, email address, phone number, clinic name, business address, professional role, registration or licence information where required, login and account details, billing information, and communication history.\n\nFor patients, we may process patient name, patient ID or case number, age or date of birth, contact details if provided, dental scans, intraoral and extraoral photographs, X-rays, CBCT or other clinical images, dental charting, treatment notes, treatment plan information, aligner stage information, IPR, attachments, trimlines, refinement information, reports, app usage related to aligner reminders or treatment progress, and QR code access information.\n\nWe may also collect technical data, including IP address, device type, browser type, operating system, app version, login times, pages or features used, error logs, security logs, cookies, and similar technologies.",
  },
  {
    number: "5",
    title: "How we collect data",
    body: "We collect data when a dentist, clinic, or authorised user creates an account; a case is submitted to UALIGN; files are uploaded to our platform; a patient uses a QR code or app; you contact us by email, phone, WhatsApp, form, or social media; you use our website or app; we receive data from authorised distributors, agents, clinics, or dental professionals; or we generate treatment planning or manufacturing records.",
  },
  {
    number: "6",
    title: "Why we use personal data",
    body: "We use personal data to provide digital treatment planning, manufacture custom aligners, manage dentist and clinic accounts, provide patient app access and reminders, generate reports and treatment records, communicate with dentists, clinics, agents, and patients, provide support and troubleshooting, process payments and invoices, improve our website, app, products, and services, maintain quality control and traceability, comply with legal, regulatory, accounting, and medical device obligations, protect security, prevent fraud or unauthorised access, and send service updates or marketing communications where permitted.",
  },
  {
    number: "7",
    title: "Lawful bases for processing",
    body: "Depending on the situation, we rely on one or more lawful bases under UK GDPR, including contract, legal obligation, legitimate interests, consent, and, rarely, vital interests.\n\nFor health-related or special category data, an additional condition is required under UK GDPR. This may include processing necessary for healthcare purposes, processing under the responsibility of a healthcare professional, explicit consent where appropriate, or legal or regulatory obligations.\n\nThe exact lawful basis may depend on whether UALIGN is acting as controller or processor.",
  },
  {
    number: "8",
    title: "Health data and dental records",
    body: "Dental scans, treatment plans, photographs, X-rays, CBCT files, clinical notes, and aligner records may reveal information about a patient's health.\n\nWe process this information only where necessary to provide UALIGN services, support the treating dental professional, manufacture custom aligners, maintain quality and traceability, comply with legal obligations, or as otherwise permitted by law.",
  },
  {
    number: "9",
    title: "Patient app and QR codes",
    body: "The UALIGN patient app and QR codes may allow patients to register or access their case, view aligner instructions, receive reminders, follow app-based guidance, access app download links, and receive treatment-related notifications.\n\nThe app is intended to support treatment prescribed by a dentist. It is not a replacement for professional dental care.",
  },
  {
    number: "10",
    title: "Sharing personal data",
    body: "We may share personal data with treating dentists and clinics; authorised dental laboratories or production partners; approved distributors or agents involved in case support; cloud hosting providers; software and IT service providers; payment processors; email, communication, and customer support tools; delivery, courier, or logistics providers; professional advisers such as lawyers, accountants, and insurers; and regulators, authorities, or courts where required by law.\n\nWe only share data where necessary and with appropriate safeguards.",
  },
  {
    number: "11",
    title: "International data transfers",
    body: "UALIGN may use service providers or partners located outside the UK. Where personal data is transferred internationally, we will use appropriate safeguards, such as adequacy regulations, standard contractual clauses, or other lawful transfer mechanisms.",
  },
  {
    number: "12",
    title: "How long we keep data",
    body: "We keep personal data only for as long as necessary for the purposes described in this policy. Retention periods may depend on clinical, legal, and regulatory requirements; medical device traceability obligations; contractual obligations with dentists or clinics; accounting and tax requirements; and the need to resolve disputes or support treatment records.\n\nTypical retention periods may include: website enquiry data for a limited period; dentist and clinic account data for the account duration and a period after closure; patient case records for a period after case completion unless a longer period is legally required; billing records for at least 6 years for UK accounting purposes; and technical logs for a limited security and troubleshooting period.",
  },
  {
    number: "13",
    title: "Security",
    body: "We use reasonable technical and organisational measures to protect personal data, including access controls, password protection, role-based permissions, secure cloud infrastructure, encryption where appropriate, backups, security monitoring, staff or contractor confidentiality obligations, and limited access to patient records.\n\nNo system is completely secure, but we work to protect personal data against unauthorised access, loss, misuse, alteration, or disclosure.",
  },
  {
    number: "14",
    title: "Cookies and analytics",
    body: "Our website may use cookies or similar technologies to keep the website working, remember preferences, understand website usage, improve performance, support security, and measure marketing effectiveness.\n\nWhere required, we will request consent for non-essential cookies. A separate Cookie Policy may be provided on our website.",
  },
  {
    number: "15",
    title: "Marketing communications",
    body: "We may send marketing or service communications to dentists, clinics, distributors, or business contacts where permitted by law.\n\nYou can opt out of marketing emails at any time by using the unsubscribe link or contacting us at info@ualign.co.uk.\n\nWe will still send important service, safety, account, or legal communications where necessary.",
  },
  {
    number: "16",
    title: "Your data protection rights",
    body: "Depending on the circumstances, you may have the right to access your personal data; correct inaccurate data; request deletion; restrict processing; object to processing; request data portability; withdraw consent where processing is based on consent; and complain to a data protection authority.\n\nSome rights may be limited where we process data on behalf of a dentist or clinic, or where we must keep data for legal, clinical, regulatory, or safety reasons.",
  },
  {
    number: "17",
    title: "How patients can exercise their rights",
    body: "If you are a patient, your treating dentist or clinic is usually the main controller of your clinical records. In many cases, you should contact your dentist or clinic first.\n\nYou may also contact UALIGN at info@ualign.co.uk. If we process your data on behalf of your dentist, we may need to refer your request to them.",
  },
  {
    number: "18",
    title: "Complaints",
    body: "If you have a concern about how we handle personal data, please contact us first at info@ualign.co.uk so we can review and respond to your concern.",
  },
  {
    number: "19",
    title: "Children",
    body: "UALIGN services may be used in relation to younger patients only under the supervision of a dentist, parent, guardian, or legally authorised person where required.\n\nThe UALIGN app is not intended for unsupervised use by children.",
  },
  {
    number: "20",
    title: "Changes to this Privacy Policy",
    body: "We may update this Privacy Policy from time to time. The latest version will be published on our website or app with the effective date.",
  },
  {
    number: "21",
    title: "Contact",
    body: "UALIGN LTD\nCompany number: 17062684\nRegistered in England and Wales\nRegistered office: 128, City Road, London, EC1V 2NX, UNITED KINGDOM\nEmail: info@ualign.co.uk\nPhone: +44 7537 135002\nWebsite: https://ualign.co.uk\nPrivacy and data protection contact: info@ualign.co.uk",
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <header className="legal-header">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </header>

      <main className="legal-content">
        <div className="legal-meta">
          <p className="legal-meta-item"><strong>Company:</strong> UALIGN LTD</p>
          <p className="legal-meta-item"><strong>Effective date:</strong> 1 January 2026</p>
          <p className="legal-meta-item"><strong>Email:</strong> info@ualign.co.uk</p>
        </div>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-subtitle">
          Website, dentist portal, patient app, QR code system and treatment planning services
        </p>

        {SECTIONS.map((section) => (
          <section key={section.number} className="legal-section">
            <h2 className="legal-section-title">
              {section.number}. {section.title}
            </h2>
            {section.body.split("\n\n").map((para, i) => (
              <p key={i} className="legal-para">
                {para}
              </p>
            ))}
          </section>
        ))}
      </main>
    </div>
  );
};

export default PrivacyPolicy;
